import { RegisterOptions, useFormContext } from "react-hook-form";

import { useGetProduct } from "queries/legacy/product-queries";
import { useGetCustomisation } from "queries/legacy/product-queries";
import { SSR_CONFIGURATION_ERROR } from "utils/error-utils";
import { CANVAS_FALLBACK_CONTENT } from "utils/fallback-content";

import DropdownSelect from "../dropdownSelect/DropdownSelect";

import styles from "./DateInput.module.scss";

type DateInputProps = {
  day: {
    label?: string;
    name: string;
  };
  month: {
    label?: string;
    name: string;
  };
  registerOptions: RegisterOptions;
  year: {
    label?: string;
    max: number;
    min: number;
    name: string;
  };
};

export default function DateInput({
  day,
  month,
  registerOptions,
  year,
}: DateInputProps) {
  const {
    formState: { errors },
    register,
  } = useFormContext();

  const { data: product } = useGetProduct();

  const { data: customisation } = useGetCustomisation();

  if (!product) {
    throw SSR_CONFIGURATION_ERROR;
  }

  const monthNames =
    product.content.canvas.month_names?.[0] ??
    CANVAS_FALLBACK_CONTENT.month_names[0]!;

  if (!customisation) {
    return null;
  }

  // All DateInputs should return numbers and validate for real dates,
  // so default those here but allow overriding if necessary
  const combinedRegisterOptions: RegisterOptions = {
    valueAsNumber: true,
    ...(registerOptions as any),
  };

  const monthOptions = Object.fromEntries(
    Object.values(monthNames).map((month, i) => [
      String(i + 1),
      month ?? String(i + 1),
    ])
  );

  const dayOptions = Object.fromEntries(
    Array.from({ length: 31 }, (_, i) => [
      String(i + 1),
      String(i + 1).padStart(2, "0"),
    ])
  );

  const years = (
    <DropdownSelect
      key={year.name}
      aria-invalid={!!errors?.[year.name]}
      aria-label={year.label}
      options={Array.from({ length: year.max - year.min + 1 }, (_, i) =>
        String(year.min + i)
      ).reverse()}
      {...register(year.name, combinedRegisterOptions)}
    />
  );

  const months = (
    <DropdownSelect
      key={month.name}
      aria-invalid={!!errors?.[month.name]}
      aria-label={month.label}
      options={monthOptions}
      {...register(month.name, combinedRegisterOptions)}
    />
  );

  const days = (
    <DropdownSelect
      key={day.name}
      aria-invalid={!!errors?.[day.name]}
      aria-label={day.label}
      options={dayOptions}
      {...register(day.name, combinedRegisterOptions)}
    />
  );

  return (
    <div className={styles.dateInput}>
      {customisation.locale === "en-US" && [months, days, years]}
      {customisation.locale !== "en-US" && [days, months, years]}
    </div>
  );
}
